<script setup>
import { ref, computed, watch } from "vue";
import AppDataTable from "@/components/AppDataTable.vue";
import { downloadCsv } from "@/lib/csv";
import { toDate } from "@/lib/date";
import { deleteCampaignResponse, fetchCmsCampaigns, fetchCampaignResponses } from "./api";

const campaigns = ref([]);
const campaignsLoading = ref(false);
const selectedCampaigns = ref([]);

const responses = ref([]);
const loading = ref(false);
const error = ref("");
const selectedResponseIds = ref([]);
const deleting = ref(false);
const deleteResponsesEnabled = import.meta.env.VITE_ENABLE_CMS_RESPONSE_DELETE === "true";

function responseIdFromSelection(selection) {
  if (selection && typeof selection === "object") {
    return selection.id || selection.value || "";
  }
  return selection || "";
}

const normalizedSelectedResponseIds = computed(() =>
  Array.from(
    new Set(
      selectedResponseIds.value
        .map((selection) => responseIdFromSelection(selection))
        .filter(Boolean)
    )
  )
);

async function loadCampaigns() {
  campaignsLoading.value = true;
  try {
    campaigns.value = await fetchCmsCampaigns();
  } catch {
    error.value = "Couldn't load campaign list.";
  } finally {
    campaignsLoading.value = false;
  }
}
loadCampaigns();

async function loadResponses() {
  selectedResponseIds.value = [];
  if (!selectedCampaigns.value.length) {
    responses.value = [];
    return;
  }
  loading.value = true;
  error.value = "";
  try {
    const results = await Promise.all(
      selectedCampaigns.value.map((slug) => fetchCampaignResponses(slug))
    );
    responses.value = results.flat();
  } catch {
    error.value = "Couldn't load responses for the selected campaigns.";
    responses.value = [];
  } finally {
    loading.value = false;
  }
}
watch(selectedCampaigns, loadResponses);

watch(responses, () => {
  const existingIds = new Set(responses.value.map((r) => r.id));
  selectedResponseIds.value = selectedResponseIds.value.filter((selection) =>
    existingIds.has(responseIdFromSelection(selection))
  );
});

/** snake_case / camelCase key -> "Title Case" label */
function prettifyLabel(key) {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Flatten arrays/objects into display-safe, CSV-safe text. */
function flattenValue(v) {
  if (v == null) return "";
  if (Array.isArray(v)) {
    return v
      .map((item) => {
        if (item && typeof item === "object") {
          if (item.url) return item.name ? `${item.name} (${item.url})` : item.url;
          return JSON.stringify(item);
        }
        return item;
      })
      .join("; ");
  }
  if (typeof v === "object") return JSON.stringify(v);
  return v;
}

/** Detect an array of uploaded-file objects (e.g. supporting documents). */
function isFileArray(v) {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((item) => item && typeof item === "object" && typeof item.url === "string")
  );
}

const IMAGE_RE = /\.(png|jpe?g|gif|webp|heic|heif|svg|bmp)$/i;
const PDF_RE = /\.pdf$/i;

function isImageFile(f) {
  return IMAGE_RE.test(f?.name || f?.url || "");
}
function isPdfFile(f) {
  return PDF_RE.test(f?.name || f?.url || "");
}

const previewFile = ref(null);
function openPreview(f) {
  previewFile.value = f;
}
function closePreview() {
  previewFile.value = null;
}

const baseColumns = [
  { key: "submittedAt", label: "Submitted At", sortable: true },
  { key: "campaignTitle", label: "Campaign Page", sortable: true },
  { key: "name", label: "Name", sortable: true },
  { key: "staffID", label: "Staff ID" },
  { key: "department", label: "Department" },
  { key: "email", label: "Email" },
];

/** Dynamic columns built from the union of `values` keys across all responses. */
const valueColumns = computed(() => {
  const keys = [];
  for (const r of responses.value) {
    for (const k of Object.keys(r.values || {})) {
      if (!keys.includes(k)) keys.push(k);
    }
  }
  return keys.map((k) => ({ key: `value_${k}`, label: prettifyLabel(k), rawKey: k }));
});

const columns = computed(() => [...baseColumns, ...valueColumns.value]);

function campaignTitle(slug) {
  return campaigns.value.find((c) => c.id === slug)?.title || slug || "";
}

const rows = computed(() =>
  responses.value.map((r) => {
    const userInfo = r.user_info || {};
    const row = {
      id: r.id,
      submittedAt: toDate(r.createdAt)?.toLocaleString() || "",
      campaignTitle: campaignTitle(r.page),
      name: userInfo.display_name || userInfo.name || "",
      staffID: userInfo.staffID || "",
      department: userInfo.department || "",
      email: userInfo.email || "",
      __raw: r.values || {},
    };
    for (const [k, v] of Object.entries(r.values || {})) {
      row[`value_${k}`] = flattenValue(v);
    }
    return row;
  })
);

const exportFilenameLabel = computed(() =>
  selectedCampaigns.value.length === 1
    ? campaignTitle(selectedCampaigns.value[0])
    : `${selectedCampaigns.value.length}-campaigns`
);

function exportCsv() {
  downloadCsv(`${exportFilenameLabel.value}-responses.csv`, columns.value, rows.value);
}

async function deleteSelectedResponses() {
  if (!deleteResponsesEnabled) {
    error.value = "Delete is not available: hr-server has no campaign response delete endpoint yet.";
    return;
  }

  if (!normalizedSelectedResponseIds.value.length || deleting.value) return;
  const confirmed = window.confirm(
    `Delete ${normalizedSelectedResponseIds.value.length} selected response(s)? This action cannot be undone.`
  );
  if (!confirmed) return;

  deleting.value = true;
  error.value = "";
  const idsToDelete = [...normalizedSelectedResponseIds.value];

  try {
    await Promise.all(idsToDelete.map((id) => deleteCampaignResponse(id)));
    responses.value = responses.value.filter((r) => !idsToDelete.includes(r.id));
    selectedResponseIds.value = [];
  } catch {
    error.value = "Couldn't delete one or more selected responses.";
    await loadResponses();
  } finally {
    deleting.value = false;
  }
}
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Campaign Responses</h1>
      <div class="d-flex gap-2 flex-wrap">
        <VBtn variant="tonal" :disabled="!selectedCampaigns.length || deleting" @click="loadResponses">Refresh</VBtn>
        <VBtn
          variant="tonal"
          color="error"
          :disabled="!deleteResponsesEnabled || !normalizedSelectedResponseIds.length || deleting || loading"
          @click="deleteSelectedResponses"
        >
          {{ deleteResponsesEnabled ? (deleting ? 'Deleting…' : `Delete Selected (${normalizedSelectedResponseIds.length})`) : 'Delete Selected (Unavailable)' }}
        </VBtn>
        <VBtn variant="tonal" :disabled="!rows.length || deleting" @click="exportCsv">Export CSV</VBtn>
      </div>
    </div>

    <VCard class="mb-4">
      <VCardText>
        <VSelect
          v-model="selectedCampaigns"
          :items="campaigns"
          item-title="title"
          item-value="id"
          label="Campaigns"
          multiple
          chips
          closable-chips
          :loading="campaignsLoading"
          clearable
          hide-details
        />
      </VCardText>
    </VCard>

    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <VAlert
      v-if="!deleteResponsesEnabled"
      type="warning"
      variant="tonal"
      class="mb-4"
    >
      Bulk delete is disabled in frontend because hr-server does not expose a campaign response delete endpoint.
      Set <strong>VITE_ENABLE_CMS_RESPONSE_DELETE=true</strong> only after backend support is deployed.
    </VAlert>

    <AppDataTable
      v-model="selectedResponseIds"
      v-if="selectedCampaigns.length"
      :columns="columns"
      :rows="rows"
      :loading="loading"
      row-key="id"
      selectable
      searchable
      search-placeholder="Search responses…"
      empty-text="No responses found for the selected campaigns."
    >
      <template v-for="col in valueColumns" :key="col.key" #[`cell-${col.key}`]="{ row }">
        <div v-if="isFileArray(row.__raw?.[col.rawKey])" class="d-flex flex-column ga-1">
          <button
            v-for="(f, i) in row.__raw[col.rawKey]"
            :key="i"
            type="button"
            class="text-primary text-left"
            style="background: none; border: none; padding: 0; cursor: pointer; text-decoration: underline"
            @click="openPreview(f)"
          >
            {{ f.name || f.url }}
          </button>
        </div>
        <template v-else>{{ row[col.key] }}</template>
      </template>
    </AppDataTable>
    <VCard v-else>
      <VCardText class="text-medium-emphasis">Select a campaign to view its responses.</VCardText>
    </VCard>

    <VDialog :model-value="!!previewFile" max-width="720" @update:model-value="closePreview">
      <VCard v-if="previewFile">
        <VCardTitle class="d-flex align-center justify-space-between">
          <span class="text-truncate">{{ previewFile.name || 'Preview' }}</span>
          <VBtn icon="ri-close-line" variant="text" size="small" @click="closePreview" />
        </VCardTitle>
        <VCardText>
          <img
            v-if="isImageFile(previewFile)"
            :src="previewFile.url"
            :alt="previewFile.name"
            style="max-width: 100%; max-height: 70vh; display: block; margin: 0 auto"
          />
          <iframe
            v-else-if="isPdfFile(previewFile)"
            :src="previewFile.url"
            style="width: 100%; height: 70vh; border: none"
          />
          <div v-else class="text-center py-6">
            <p class="text-medium-emphasis mb-3">Preview isn't available for this file type.</p>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn :href="previewFile.url" target="_blank" rel="noopener" variant="tonal">Open Original</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
